import { Button, Input } from '@invent/wl-ui-kit-next/ui';
import { Plus, Trash2 } from 'lucide-react';
import * as React from 'react';

import type {
  IWriteInternalFieldOption,
  InternalFieldAccess
} from '../../api/internal-catalogue-contracts';

/**
 * The pieces both staff-field screens draw, in one place (SR-226) — the values a choice field
 * offers, the access vocabulary, and the sentinel a kit `Select` needs.
 *
 * The create form and the field's card edit the same `IWriteInternalFieldOption[]` with the same
 * gestures, and the E2E suite addresses the boxes by the accessible names below: spelled twice
 * they would drift apart, and a spec would pass against one screen and not the other.
 */

/** A kit `Select` cannot hold an empty string as an item's value, so "nothing chosen" needs a
 *  word. One word, so the three pickers that need it cannot disagree. */
export const SELECT_NONE = 'none';

/** Who may see a field (`InternalFieldAccess`), as both screens offer it — the repo's "one
 *  rendering per thing two screens show" rule. */
export const ACCESS_OPTIONS: { value: InternalFieldAccess; label: string }[] = [
  { value: 'staff', label: 'Staff and administrators' },
  { value: 'admin', label: 'Administrators only' }
];

/**
 * A choice field's values, in order — the array position is the ordinal survey-api stores.
 *
 * `retirable` is the one difference between the two callers: a field being created has nothing
 * stored under any of its values, so there is nothing to retire; a field that exists may have,
 * and a value some record holds cannot be removed (`400 option-in-use`) — retiring keeps it
 * labelling what it labelled.
 */
export function OptionRows({
  rows,
  retirable,
  onChange
}: {
  rows: IWriteInternalFieldOption[];
  retirable: boolean;
  onChange: (rows: IWriteInternalFieldOption[]) => void;
}) {
  const edit = (index: number, change: Partial<IWriteInternalFieldOption>) =>
    onChange(rows.map((row, at) => (at === index ? { ...row, ...change } : row)));
  const struck = (row: IWriteInternalFieldOption) => (row.retired === true ? ' line-through' : '');

  return (
    <>
      {rows.map((row, index) => (
        <div key={index} className="flex flex-wrap items-center gap-2">
          <Input
            value={row.code}
            onChange={(event) => edit(index, { code: event.target.value })}
            placeholder="code"
            className={`w-40 font-mono${struck(row)}`}
            aria-label={`Value ${index + 1} code`}
          />
          <Input
            value={row.label}
            onChange={(event) => edit(index, { label: event.target.value })}
            placeholder="What a reader sees"
            className={`max-w-sm flex-1${struck(row)}`}
            aria-label={`Value ${index + 1} label`}
          />
          {retirable && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => edit(index, { retired: row.retired !== true })}
            >
              {row.retired === true ? 'Bring back' : 'Retire'}
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label={`Remove value ${index + 1}`}
            onClick={() => onChange(rows.filter((_, at) => at !== index))}
          >
            <Trash2 />
          </Button>
        </div>
      ))}
      <div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onChange([...rows, { code: '', label: '' }])}
        >
          <Plus />
          Add a value
        </Button>
      </div>
    </>
  );
}
